/**
 * API Client for ComDesBook Backend
 * Handles all HTTP requests with authentication
 */

import { APP, STORAGE_KEYS } from '../config/app';
import { announceSessionEnded, isSessionEnd } from './sessionEnd';

// On production or Cloudflare Pages, use relative URL (same origin — D1 backend)
// On localhost, use VITE_API_URL (points to wrangler pages dev on :8788)
const hostname = window.location.hostname;
const isCloudflare = hostname.includes('pages.dev') || APP.isProduction(hostname);
export const API_BASE_URL = isCloudflare
  ? '/api'
  : (import.meta.env.VITE_API_URL || '/api');

/**
 * Get auth token from localStorage
 */
const getToken = () => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

/**
 * Set auth token in localStorage
 */
const setToken = (token) => {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
};

/**
 * Remove auth token from localStorage
 */
const removeToken = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
};

/**
 * Normalize Hono backend responses to match frontend expectations
 * Hono returns { success, data } but components expect { equipment: [...] }, { bookings: [...] }, etc.
 */
/**
 * Normalize Hono backend responses to match frontend expectations
 *
 * The Hono backend returns various shapes:
 *   { success, data: [...] }              — equipment, bookings
 *   { success, data: { users: [...] } }   — users, departments (nested)
 *   { success, settings: [...] }          — already has named key
 *
 * The frontend expects named keys: response.equipment, response.bookings, etc.
 */
function normalizeResponse(endpoint, data) {
  if (!data || !data.success) return data;

  // Map endpoint prefixes to the key names the frontend expects
  const endpointKeyMap = {
    '/equipment': 'equipment',
    '/bookings': 'bookings',
    '/users': 'users',
    '/departments': 'departments',
    '/kits': 'kits',
    '/settings': 'settings',
    '/fines': 'fines',
    '/analytics': 'analytics',
    '/strikes': 'strikes',
    '/staff-permissions': 'staff_permissions',
    '/spaces': 'spaces',
    '/space-bookings': 'space_bookings',
  };

  // Find matching endpoint prefix
  for (const [prefix, key] of Object.entries(endpointKeyMap)) {
    if (endpoint.startsWith(prefix)) {
      const normalized = { ...data };

      // Case 1: { data: [...] } — flat array in data
      if (Array.isArray(data.data)) {
        normalized[key] = data.data;
      }
      // Case 2: { data: { users: [...] } } — nested object with named key
      else if (data.data && typeof data.data === 'object' && data.data[key]) {
        normalized[key] = data.data[key];
        // Copy other fields from nested data (e.g., total, pagination)
        for (const [k, v] of Object.entries(data.data)) {
          if (k !== key) normalized[k] = v;
        }
      }
      // Case 3: already has the named key (e.g., { settings: [...] })
      // — no action needed

      return normalized;
    }
  }

  return data;
}

/**
 * Make authenticated API request
 */
const request = async (endpoint, options = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.text();
    }

    const data = await response.json();

    if (!response.ok) {
      // A refused session (suspended, password or email changed elsewhere)
      // signs the tab out with a reason, instead of every screen showing the
      // raw error. See utils/sessionEnd.js.
      if (isSessionEnd(response.status, endpoint, !!token)) {
        announceSessionEnded(data.error);
      }
      // Callers that need more than the message (a 409's `conflict`, say) read
      // the status and body off the error.
      const error = new Error(data.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data; // the full parsed response body, not just `error`

      throw error;
    }

    // Normalize Hono { success, data } responses to match frontend expectations
    return normalizeResponse(endpoint, data);
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// ============================================
// AUTHENTICATION API
// ============================================

export const authAPI = {
  /**
   * Login with email and password
   */
  login: async (email, password) => {
    const data = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Register new user (Master Admin only)
   */
  register: async (userData) => {
    return await request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async () => {
    return await request('/auth/me');
  },

  /**
   * Update password
   */
  updatePassword: async (currentPassword, newPassword) => {
    const data = await request('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
    // A password change ends every session minted under the old password,
    // this one included; the server sends a replacement for this device.
    if (data?.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Generate password setup tokens for one or more users (admin-only)
   */
  generateSetupTokens: async (userIds) => {
    return await request('/auth/generate-setup-tokens', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds }),
    });
  },

  /**
   * Record that setup emails actually reached these users. Called after
   * delivery is accepted, so the roster's "invited" badge reflects what was
   * sent rather than what was merely attempted.
   */
  markSetupEmailsSent: async (userIds) => {
    return await request('/auth/mark-setup-emails-sent', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds }),
    });
  },

  /**
   * Queue setup emails for the server to send within the daily email limit.
   * Returns { queued, skipped, sendableToday, estimatedDays }.
   */
  queueSetupEmails: async (userIds) => {
    return await request('/auth/setup-emails/queue', {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    });
  },

  /**
   * How many queued setup emails are still waiting, among the people you manage.
   */
  queuedSetupEmails: async () => {
    return await request('/auth/setup-emails/queue');
  },

  /**
   * Validate a setup token (public)
   */
  validateSetupToken: async (token) => {
    return await request(`/auth/validate-setup-token/${encodeURIComponent(token)}`);
  },

  /**
   * Set password using a setup token (public)
   */
  setupPassword: async (token, newPassword) => {
    return await request('/auth/setup-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },

  /**
   * Request a password reset email (public)
   */
  forgotPassword: async (email) => {
    return await request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Validate a password reset token (public)
   */
  validateResetToken: async (token) => {
    return await request(`/auth/validate-reset-token/${encodeURIComponent(token)}`);
  },

  /**
   * Reset password using a reset token (public)
   */
  resetPassword: async (token, newPassword) => {
    return await request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },

  /**
   * Impersonate a user (master admin only).
   * Returns { token, user } for the target user.
   */
  impersonate: async (userId) => {
    return await request(`/auth/impersonate/${userId}`, { method: 'POST' });
  },

  /**
   * Logout
   */
  logout: () => {
    removeToken();
    window.location.href = `${APP.basePath}/`;
  },
};

// ============================================
// DEVICE TOKENS API
//
// The credential a phone keeps so biometric sign-in never holds a password.
// `issue` and `exchange` each return a token exactly once; it belongs in the
// platform secure store and nowhere else — never localStorage, never a log.
// ============================================

export const deviceTokensAPI = {
  /** POST /device-tokens — register this device (caller must be signed in). */
  issue: async ({ device_name, platform }) => {
    return await request('/device-tokens', {
      method: 'POST',
      body: JSON.stringify({ device_name, platform }),
    });
  },

  /**
   * POST /device-tokens/exchange — a device token for a session (public).
   * Stores the session JWT exactly as `authAPI.login` does, so what the app
   * sees afterwards cannot differ between the two ways in.
   */
  exchange: async (deviceToken) => {
    const data = await request('/device-tokens/exchange', {
      method: 'POST',
      body: JSON.stringify({ token: deviceToken }),
    });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /** GET /device-tokens — this user's signed-in devices. Never hashes. */
  list: async () => await request('/device-tokens'),

  /** DELETE /device-tokens/:id — revoke one device. */
  revoke: async (id) => await request(`/device-tokens/${id}`, { method: 'DELETE' }),
};

// ============================================
// EQUIPMENT API
// ============================================

export const equipmentAPI = {
  /**
   * Get all equipment with optional filters
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/equipment?${queryString}` : '/equipment';
    return await request(endpoint);
  },

  /**
   * Resolve a scanned barcode to equipment (admin only).
   * Matches tracking_number, qr_code, or legacy numeric id.
   * Returns { equipment, active_loan, pending_bookings }.
   */
  lookupByCode: (code) => request(`/equipment/by-code/${encodeURIComponent(code)}`),

  /**
   * How often each unit in the caller's own department was booked lately.
   * Counts only — [{ id, bookings }], most-booked first. Never who booked.
   */
  getPopular: ({ days = 30, limit = 10 } = {}) =>
    request(`/equipment/popular?${new URLSearchParams({ days, limit })}`),

  /**
   * Get single equipment by ID
   */
  getById: async (id) => {
    return await request(`/equipment/${id}`);
  },

  /**
   * Who has this item (admin tiers only): { current_loan, next_booking,
   * last_returned, today }. 404 for an item outside the viewer's scope.
   */
  getLoanStatus: (id) => request(`/equipment/${encodeURIComponent(id)}/loan-status`),

  /**
   * Check equipment availability
   */
  checkAvailability: async (id, startDate, endDate) => {
    return await request(
      `/equipment/${id}/availability?start_date=${startDate}&end_date=${endDate}`
    );
  },

  /**
   * Ids of in-scope units already booked across a date window — one request for
   * the whole catalogue, so the model-grouped booking desk can show "4 of 6
   * available" without probing each unit.
   * Returns { data: { start_date, end_date, unavailable_ids: [] } }.
   */
  getWindowAvailability: async (startDate, endDate) => {
    const qs = new URLSearchParams({ start_date: startDate, end_date: endDate }).toString();
    return await request(`/equipment/availability?${qs}`);
  },

  /**
   * Create new equipment (Admin only)
   */
  create: async (equipmentData) => {
    return await request('/equipment', {
      method: 'POST',
      body: JSON.stringify(equipmentData),
    });
  },

  /**
   * Update equipment (Admin only)
   */
  update: async (id, equipmentData) => {
    return await request(`/equipment/${id}`, {
      method: 'PUT',
      body: JSON.stringify(equipmentData),
    });
  },

  /**
   * Delete equipment (Master Admin only)
   */
  delete: async (id) => {
    return await request(`/equipment/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get equipment notes (Admin only)
   */
  getNotes: async (id) => {
    return await request(`/equipment/${id}/notes`);
  },

  /**
   * Add equipment note (Admin only)
   */
  addNote: async (id, noteData) => {
    return await request(`/equipment/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
  },

  /**
   * Delete one equipment note (Admin only)
   */
  deleteNote: (id, noteId) => request(`/equipment/${id}/notes/${noteId}`, { method: 'DELETE' }),
};

// ============================================
// BOOKINGS API (Placeholder - to be implemented)
// ============================================

/**
 * In-app notifications — the admin channel that replaced admin email.
 * Rows are written server-side only; there is no create method by design.
 */
export const notificationsAPI = {
  list: async ({ unread = false, since = null, limit = 30 } = {}) => {
    const params = new URLSearchParams();
    if (unread) params.set('unread', '1');
    if (since != null) params.set('since', String(since));
    params.set('limit', String(limit));
    return await request(`/notifications?${params.toString()}`);
  },

  markRead: async (id) => request(`/notifications/${id}/read`, { method: 'PUT' }),

  markAllRead: async () => request('/notifications/read-all', { method: 'PUT' }),
};

export const bookingsAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/bookings?${queryString}` : '/bookings';
    return await request(endpoint);
  },

  getById: async (id) => {
    return await request(`/bookings/${id}`);
  },

  create: async (bookingData) => {
    return await request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  },

  /**
   * Open a checkout group for a caller that must submit line by line (the
   * /student/book desk re-allocates a line whose unit is taken mid-basket).
   * Pass the returned id as `group_id` on every
   * create, then call completeGroup — the confirmation email and the admin
   * notification fire once, at completion.
   *
   * `owner` is a user id (the desk booking for someone), or for a walk-in
   * guest `{ guest: true, guest_email?, guest_confirmed? }`.
   */
  openGroup: async (owner = null) => request('/bookings/groups', {
    method: 'POST',
    body: JSON.stringify(owner && typeof owner === 'object' ? owner : owner ? { user_id: owner } : {}),
  }),

  /** Every line is in: send the one confirmation for this checkout. */
  completeGroup: async (groupId) =>
    request(`/bookings/groups/${groupId}/complete`, { method: 'PUT' }),

  /**
   * Admin creates a booking on behalf of a student. Auto-approved.
   * Optionally checks out in the same request.
   * @param {Object} data { user_id, equipment_id, start_date, end_date, purpose, checkout_now, condition_rating, condition_notes }
   */
  adminCreate: async (data) => {
    return await request('/bookings/admin-create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  approve: async (id) => {
    return await request(`/bookings/${id}/approve`, {
      method: 'PUT',
    });
  },

  deny: async (id, reason) => {
    return await request(`/bookings/${id}/deny`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  },

  return: async (id, data = {}) => {
    return await request(`/bookings/${id}/return`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Cancel all bookings in a recurring series (parent + all children)
   * @param {string|number} id - Parent booking ID (or any child ID — backend resolves)
   */
  cancelSeries: (id) => request(`/bookings/${id}/series`, { method: 'DELETE' }),

  /**
   * Record physical equipment checkout (admin only).
   * Stamps checked_out_at; keeps status as 'approved'.
   */
  checkout: (id, data = {}) => request(`/bookings/${id}/checkout`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  /**
   * Book out every approved item a student is collecting, in one request
   * (admin only). All-or-nothing: if any booking is rejected, none are stamped
   * and the thrown error names the offending item and why.
   * @param {Array<string|number>} bookingIds - max 50, all for the same student
   * @param {Object} data { condition_rating, condition_notes }
   */
  checkoutBatch: (bookingIds, data = {}) => request('/bookings/checkout-batch', {
    method: 'POST',
    body: JSON.stringify({ ...data, booking_ids: bookingIds }),
  }),

  /**
   * Reverse a checkout that was scanned in error (admin only).
   * Clears checked_out_at; booking goes back to "awaiting collection".
   */
  undoCheckout: (id) => request(`/bookings/${id}/undo-checkout`, {
    method: 'PUT',
  }),

  /**
   * Lookup a user by scanned card value (email, name fragment, or numeric ID).
   * Returns { user, bookings } where bookings are approved/pending items.
   * `bookable: true` is for a picker that books FOR the person: the server
   * then answers only with an active student or staff member.
   */
  lookupUser: (query, { bookable = false } = {}) =>
    request(`/users/lookup?q=${encodeURIComponent(query)}${bookable ? '&bookable=1' : ''}`),

  /**
   * Cancel a single booking. Backend (DELETE /bookings/:id) lets a student
   * cancel their own pending booking; admins can cancel any.
   * @param {string|number} id - Booking ID
   */
  delete: (id) => request(`/bookings/${id}`, { method: 'DELETE' }),

  // ── Changing the unit on a booking that already exists ────────────────────
  // The desk booked "250D No1" and meant "250D No5". The server decides what
  // may be swapped for what; these two just ask.

  /**
   * The units this booking could be moved to (admin only).
   * Resolves { data: { changeable, units: [{ id, product_name, category,
   * tracking_number, label, current, free, status }] } }.
   */
  alternatives: (id) => request(`/bookings/${id}/alternatives`),

  /**
   * Move a booking onto a different unit, keeping its dates, status,
   * approval and accessories (admin only). A refusal throws an Error whose
   * `.status` is the HTTP code and whose `.data` is the whole body — a 409
   * carries `conflict: { booking_id, start_date, end_date }`.
   * @param {string|number} equipmentId - the unit to move the booking onto
   */
  // `extra` carries `confirm_in_hand: true` when the loan is already collected.
  changeEquipment: (id, equipmentId, extra = {}) => request(`/bookings/${id}/equipment`, {
    method: 'PUT',
    body: JSON.stringify({ equipment_id: equipmentId, ...extra }),
  }),

  /**
   * Move a KIT booking onto another bag (admin only) — the kit sibling of
   * changeEquipment. `extra` carries `confirm_in_hand: true` on a collected loan.
   */
  changeKit: (id, kitId, extra = {}) => request(`/bookings/${id}/kit`, {
    method: 'PUT',
    body: JSON.stringify({ kit_id: kitId, ...extra }),
  }),

  /**
   * Move or extend a booking's dates (admin only). `dates` is
   * { start_date?, end_date? } as YYYY-MM-DD; "extend" sends only end_date.
   * An administrator who manages the item moves it through a closure without
   * asking (owner rule 2026-09-28). A refusal throws with `.data` — a clash
   * carries `conflict`, a closure `blackout`.
   */
  changeDates: (id, dates, extra = {}) => request(`/bookings/${id}/dates`, {
    method: 'PUT',
    body: JSON.stringify({ ...dates, ...extra }),
  }),

  /**
   * Move several of ONE person's bookings together (admin only), all or
   * nothing. `dates` is { extend_days } (each item's own return date moves by
   * n days) or { start_date?, end_date? }. A refusal throws with `.data` — a
   * 409 carries `failures: [{ booking_id, item_name, error, conflict?,
   * blackout? }]` and nothing was written.
   */
  batchDates: (bookingIds, dates, extra = {}) => request('/bookings/batch-dates', {
    method: 'PUT',
    body: JSON.stringify({ booking_ids: bookingIds, ...dates, ...extra }),
  }),

  /**
   * The person's live bookings an admin may edit alongside this one, each
   * flagged `current` and `together` (taken out at the same hand-over).
   */
  party: (id) => request(`/bookings/${id}/party`),

  /**
   * Extending a collected loan (admin only): GET what the Extend dialog needs
   * (the checkout's other loans, whether the borrower can be emailed, any late
   * fine), PUT { end_date, all_in_checkout?, email_borrower? } to extend.
   */
  extendInfo: (id) => request(`/bookings/${id}/extend`),
  extend: (id, body) => request(`/bookings/${id}/extend`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }),

  // ── Small accessories on a booking ────────────────────────────────────────
  // Cables, adapters, card readers, SD cards, batteries. The server decides
  // whether a line is `issued` (an in-scope admin added it at the desk) or
  // `requested` (the student asked for it and self-service is on), so nothing
  // here mirrors that rule.

  /** Add one accessory line. @param {{catalogue_id:number, quantity:number}} line */
  addAccessory: (bookingId, line) => request(`/bookings/${bookingId}/accessories`, {
    method: 'POST',
    body: JSON.stringify(line),
  }),

  /** Change a line's quantity, or confirm a requested line as issued (admin). */
  updateAccessory: (bookingId, lineId, data) => request(`/bookings/${bookingId}/accessories/${lineId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  removeAccessory: (bookingId, lineId) => request(`/bookings/${bookingId}/accessories/${lineId}`, {
    method: 'DELETE',
  }),

  /** Tick a line back in at the returns desk, or untick it. */
  returnAccessory: (bookingId, lineId, returned) => request(`/bookings/${bookingId}/accessories/${lineId}/return`, {
    method: 'PUT',
    body: JSON.stringify({ returned }),
  }),
};

// ============================================
// ACCESSORY CATALOGUE API
// One list of small accessories per TOP-LEVEL department, shared by every TO
// area inside it. Scope is enforced server-side; these methods just call.
// ============================================

export const accessoryCatalogueAPI = {
  /** The manage view: every entry (active or not) plus the self-service switch. */
  getForDepartment: (departmentId) => request(
    departmentId
      ? `/accessory-catalogue?department_id=${encodeURIComponent(departmentId)}`
      : '/accessory-catalogue'
  ),

  /** The active entries a desk or student may pick for an existing booking. */
  getForBooking: (bookingId) => request(`/accessory-catalogue/for-booking/${bookingId}`),

  /** The active entries for an item's department, before any booking exists. */
  getForEquipment: (equipmentId) => request(`/accessory-catalogue/for-equipment/${equipmentId}`),

  /**
   * One answer for a whole basket: which department each item belongs to, and
   * per distinct department its switch and its list. Replaces a request per
   * basket line.
   * @param {Array<string|number>} equipmentIds
   */
  getForDepartments: (equipmentIds) => request(
    `/accessory-catalogue/for-departments?equipment_ids=${encodeURIComponent(equipmentIds.join(','))}`
  ),

  /** Save the whole order in one request — two racing PUTs shuffled the list. */
  reorder: (ids) => request('/accessory-catalogue/reorder', {
    method: 'PUT',
    body: JSON.stringify({ ids }),
  }),

  create: (data) => request('/accessory-catalogue', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => request(`/accessory-catalogue/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  /** Soft delete — the entry stops being offered, history keeps its name. */
  remove: (id) => request(`/accessory-catalogue/${id}`, { method: 'DELETE' }),
};

// ============================================
// USERS API (Placeholder - to be implemented)
// ============================================

export const usersAPI = {
  /**
   * Get all users with optional filters
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/users?${queryString}` : '/users';
    return await request(endpoint);
  },

  /**
   * Get user by ID
   */
  getById: async (id) => {
    return await request(`/users/${id}`);
  },

  /**
   * Create new user (Master Admin only)
   */
  create: async (userData) => {
    return await request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  /**
   * Update user (Master Admin only)
   */
  update: async (id, userData) => {
    return await request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  /**
   * One person's booking limits: { weekly, concurrent }. A blank or null member
   * removes that override and puts them back on their department's limit.
   */
  updateBookingLimits: async (id, limits) => {
    return await request(`/users/${id}/booking-limits`, {
      method: 'PUT',
      body: JSON.stringify(limits),
    });
  },

  /**
   * Email this person a password reset link (to their own inbox; the link is
   * never returned). A TO for their students and staff, the master for anyone.
   */
  sendPasswordReset: async (id) => {
    return await request(`/users/${id}/password-reset`, { method: 'POST' });
  },

  /**
   * Delete user (Master Admin only)
   */
  delete: async (id) => {
    return await request(`/users/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Anonymise a person in place, keeping their booking records (Master Admin
   * only). The answer to a delete refused because records point at the
   * account; also marks their pending deletion request completed.
   * @param {string|number} id
   * @param {{ email?: string, note?: string }} [options] a replacement address
   *   the master chooses; blank means anonymised-<id>@deleted.invalid
   */
  anonymise: async (id, options = {}) => {
    return await request(`/users/${id}/anonymise`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },

  /**
   * Add strike to user (Admin only)
   */
  addStrike: async (userId, strikeData) => {
    return await request(`/users/${userId}/strikes`, {
      method: 'POST',
      body: JSON.stringify(strikeData),
    });
  },

  /**
   * Suspend a user account (Admin only)
   * @param {string|number} id - User ID
   * @param {string} reason - Optional reason for suspension
   */
  suspend: async (id, reason) => {
    return await request(`/users/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Reactivate a suspended user account (Admin only)
   * @param {string|number} id - User ID
   */
  reactivate: async (id) => {
    return await request(`/users/${id}/reactivate`, {
      method: 'POST',
    });
  },

  /**
   * Get user activity timeline (Admin only)
   * @param {string} userId
   * @param {Object} params - Optional { limit, offset }
   */
  getActivity: async (userId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(qs ? `/users/${userId}/activity?${qs}` : `/users/${userId}/activity`);
  },

  /**
   * GDPR Subject Access Request — export all personal data for the current user
   */
  exportMyData: async () => {
    return await request('/users/me/data-export');
  },

  /**
   * The signed-in user's most recent account-deletion request, or null.
   * @returns {Promise<{ success: boolean, data: { deletion_request: object|null } }>}
   */
  getMyDeletionRequest: async () => {
    return await request('/users/me/deletion-request');
  },

  /**
   * Ask an administrator to delete this account. One pending request per user.
   * @param {string} [reason] optional free text, capped server-side at 500 chars
   */
  requestAccountDeletion: async (reason) => {
    return await request('/users/me/deletion-request', {
      method: 'POST',
      body: JSON.stringify({ reason: reason || null }),
    });
  },

  /** Withdraw your own pending deletion request. */
  cancelMyDeletionRequest: async () => {
    return await request('/users/me/deletion-request', { method: 'DELETE' });
  },

  /** Pending account-deletion requests with loan/fine counts (Master Admin only). */
  listDeletionRequests: async () => {
    return await request('/users/deletion-requests');
  },

  /**
   * Record that a deletion request has been dealt with (Master Admin only).
   * This does NOT delete the account — use usersAPI.delete for that.
   * @param {string|number} id
   * @param {'completed'|'rejected'} status
   * @param {string} [note]
   */
  resolveDeletionRequest: async (id, status, note) => {
    return await request(`/users/deletion-requests/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ status, note: note || null }),
    });
  },
};

// ============================================
// DEPARTMENTS API
// ============================================

export const departmentsAPI = {
  /**
   * Get all departments/sub-areas
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/departments?${queryString}` : '/departments';
    return await request(endpoint);
  },

  /**
   * Create new department (Master Admin only)
   */
  create: async (departmentData) => {
    return await request('/departments', {
      method: 'POST',
      body: JSON.stringify(departmentData),
    });
  },

  /**
   * Update department (Master Admin only)
   */
  update: async (id, departmentData) => {
    return await request(`/departments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(departmentData),
    });
  },

  /**
   * Delete department (Master Admin only)
   */
  delete: async (id) => {
    return await request(`/departments/${id}`, {
      method: 'DELETE',
    });
  },

};

// ============================================
// ANALYTICS API
// ============================================

export const analyticsAPI = {
  /**
   * Get dashboard analytics
   * @param {Object} params - Optional filters (start_date, end_date, department)
   */
  getDashboard: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/analytics/dashboard?${queryString}` : '/analytics/dashboard';
    return await request(endpoint);
  },

  /**
   * Export analytics to CSV
   * @param {Object} params - Filters and type (start_date, end_date, department, type: 'bookings'|'equipment')
   */
  exportCSV: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/analytics/export/csv?${queryString}` : '/analytics/export/csv';

    const token = getToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export CSV');
    }

    // Trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return { success: true };
  },

  /**
   * Get per-department comparison: bookings, utilisation, fines
   * @param {Object} params - Optional filters (start_date, end_date)
   */
  getDepartmentStats: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/analytics/departments?${queryString}` : '/analytics/departments';
    return await request(endpoint);
  },

  /**
   * Get high-demand / low-supply equipment alerts for the next 14 days
   * Returns equipment with >80% utilisation in the upcoming 14-day window
   */
  getAlerts: async () => {
    return await request('/analytics/alerts');
  },
};

// ============================================
// SYSTEM SETTINGS API
// ============================================

export const systemSettingsAPI = {
  /**
   * Get single setting by key
   */
  getByKey: async (key) => {
    return await request(`/settings/${key}`);
  },

  /**
   * Update or create system setting (Master Admin only)
   */
  update: async (key, value, description) => {
    return await request(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value, description }),
    });
  },
};

// ============================================
// DEPARTMENT SETTINGS API
// ============================================

export const departmentSettingsAPI = {
  // Exactly the areas the signed-in admin may switch student room booking for,
  // with each one's current value. The server's own answer — the browser does
  // not work the department scope out for itself.
  studentRoomBookingAreas: async () => {
    return await request('/department-settings/student-room-booking/areas');
  },

  getAll: async (departmentId) => {
    return await request(`/department-settings/${departmentId}`);
  },

  getByKey: async (departmentId, key) => {
    return await request(`/department-settings/${departmentId}/${key}`);
  },

  update: async (departmentId, key, value) => {
    return await request(`/department-settings/${departmentId}/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  delete: async (departmentId, key) => {
    return await request(`/department-settings/${departmentId}/${key}`, {
      method: 'DELETE',
    });
  },

  /**
   * The four booking limits for a department, read and written together.
   * Omit departmentId to act on the caller's own department.
   */
  getBookingLimits: async (departmentId) => {
    const query = departmentId ? `?department=${encodeURIComponent(departmentId)}` : '';
    return await request(`/department-settings/booking-limits${query}`);
  },

  /** `limits` holds any of the four keys; a blank or null value clears one. */
  updateBookingLimits: async (departmentId, limits) => {
    return await request('/department-settings/booking-limits', {
      method: 'PUT',
      body: JSON.stringify({ department_id: departmentId, ...limits }),
    });
  },
};

// ============================================
// LAB COMPUTERS API (FAM Computer Lab / FAMControl mirror)
// ============================================

export const labComputersAPI = {
  /**
   * List lab computers. Admin-only, department-scoped to 'complab' on the
   * backend (master/HoD or a dept admin whose subtree contains 'complab').
   * Returns { success, data: [...] }; throws on 403 for out-of-scope admins.
   */
  getAll: async () => {
    return await request('/lab-computers');
  },
};

// ============================================
// TRAINING / INDUCTION RECORDS API
// ============================================

export const trainingRecordsAPI = {
  /**
   * List induction records (admin-only, subtree-scoped). Optional filters.
   * @param {Object} params - { user_id?, sub_area_id? }
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/training-records?${queryString}` : '/training-records';
    return await request(endpoint);
  },

  /**
   * Create an induction record. 201 on success; 409 if a duplicate already
   * exists; 403 if the sub-area is outside the caller's subtree.
   * @param {Object} data - { user_id, sub_area_id, training_type, notes?, completed_at? }
   */
  create: async (data) => {
    return await request('/training-records', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete an induction record by id (admin-only, subtree-scoped).
   */
  delete: async (id) => {
    return await request(`/training-records/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * The signed-in student's OWN induction records (source: manual | session |
   * excused). Read-only, self-scoped — powers the student "My inductions" list.
   */
  getMine: async () => {
    return await request('/training-records/mine');
  },
};

// ============================================
// INDUCTION TYPES API (per-sub-area named inductions)
// ============================================

/**
 * Induction types — the named inductions a TO defines per sub-area leaf (e.g.
 * "Greenscreen"). Listing is open to any authenticated user but scoped: admins
 * see their subtree, students see their family, master sees all. Writes are
 * subtree-gated on the backend.
 */
export const inductionTypesAPI = {
  /** List types. @param {Object} params - { sub_area_id?, active? } (active: '1'|'0') */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/induction-types?${queryString}` : '/induction-types';
    return await request(endpoint);
  },

  /** Create a type. @param {Object} data - { sub_area_id, name, description? } */
  create: async (data) => {
    return await request('/induction-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Update a type. @param {Object} data - { name?, description?, active? } */
  update: async (id, data) => {
    return await request(`/induction-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a type. 409 when it is referenced by equipment/spaces/sessions —
   * the UI then offers to deactivate instead.
   */
  delete: async (id) => {
    return await request(`/induction-types/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// INDUCTION / DEMO SESSIONS API (attendance)
// ============================================

/**
 * Induction & demo sessions — a TO/HoD/master schedules a session, opens sign-in
 * (server mints a 6-char code, live only while open), attendees self-sign-in with
 * the in-room code, then the TO completes it (bulk-creates records for inductions;
 * attendance only for demos). Admin-subtree scoped; HoD reaches the report read-only.
 */
export const inductionSessionsAPI = {
  /** List sessions. @param {Object} params - { status?, kind?, induction_type_id? } */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/induction-sessions?${queryString}` : '/induction-sessions';
    return await request(endpoint);
  },

  /** Session detail incl. attendance list; signin_code present ONLY while open. */
  getById: async (id) => {
    return await request(`/induction-sessions/${id}`);
  },

  /**
   * Create a session.
   * @param {Object} data - induction: { induction_type_id, starts_at, location?, share_emails? }
   *                         demo:      { kind:'demo', sub_area_id, title, starts_at, location?, share_emails? }
   */
  create: async (data) => {
    return await request('/induction-sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Open sign-in → { signin_code }. Code is only retrievable while open. */
  openSignin: async (id) => {
    return await request(`/induction-sessions/${id}/open-signin`, { method: 'PATCH' });
  },

  /** Close sign-in (code is cleared server-side). */
  closeSignin: async (id) => {
    return await request(`/induction-sessions/${id}/close-signin`, { method: 'PATCH' });
  },

  /** Complete → { records_created, report_emailed_to }. */
  complete: async (id) => {
    return await request(`/induction-sessions/${id}/complete`, { method: 'PATCH' });
  },

  /** Cancel a session (not once completed). */
  cancel: async (id) => {
    return await request(`/induction-sessions/${id}/cancel`, { method: 'PATCH' });
  },

  /** Strike an attendee off before completion. */
  removeAttendee: async (id, userId) => {
    return await request(`/induction-sessions/${id}/attendance/${userId}`, {
      method: 'DELETE',
    });
  },

  /** Attendance report: { data: { session, attendees: [{ name, email, department, signed_in_at }] } } */
  getReport: (id) => request(`/induction-sessions/${id}/report`),
};

// ============================================
// ATTENDANCE SIGN-IN API (global, code-only)
// ============================================

/**
 * Global, session-agnostic attendance sign-in. ANY authenticated user who enters
 * a currently-open in-room code is signed in — possession of the code is the
 * authorisation (no department/family gate).
 */
export const attendanceAPI = {
  /** Sign in with an in-room code. 201 { session_id }; 403 wrong/closed; 409 already in. */
  signin: async (code) => {
    return await request('/attendance/signin', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },
};

// ============================================
// INDUCTION REQUESTS API (excuse / re-run)
// ============================================

/**
 * Student requests to be excused from, or to re-run, a named induction. Filing is
 * family-gated; listing/deciding is admin-subtree scoped.
 */
export const inductionRequestsAPI = {
  /** File a request. @param {Object} data - { induction_type_id, kind: 'excuse'|'rerun', reason? }. 409 on dup. */
  create: async (data) => {
    return await request('/induction-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** The student's own requests (with status). */
  getMine: async () => {
    return await request('/induction-requests/mine');
  },

  /** Admin subtree list. @param {Object} params - { status?, kind? } */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/induction-requests?${queryString}` : '/induction-requests';
    return await request(endpoint);
  },

  /** Decide a request. @param {Object} data - { status: 'approved'|'denied', staff_note? } */
  update: async (id, data) => {
    return await request(`/induction-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// PRINT ROOM API (FAM print sub-area)
// ============================================

/**
 * Print jobs — student digital intake for the FAM print room. Students submit a
 * spec (no upload), the backend preflights it against a brief, and the print TO
 * walks it through submitted→accepted→printing→ready→collected.
 */
export const printJobsAPI = {
  /** Submit a print job. Returns { data: job, findings[] }. */
  create: async (data) => {
    return await request('/print-jobs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** The caller's own jobs. Returns { data: [...] }. */
  getMine: async () => {
    return await request('/print-jobs/mine');
  },

  /**
   * The print queue (print TO / FAM HoD read-only / master). Optional status
   * filter. Returns { data: [...], can_manage }; throws 403 for out-of-scope.
   */
  getQueue: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(`/print-jobs${qs ? `?${qs}` : ''}`);
  },

  /** A single job (owner or a print-room reader). */
  getById: async (id) => {
    return await request(`/print-jobs/${id}`);
  },

  /**
   * Advance the workflow (print TO / master). Returns { data, low_stock[],
   * low_stock_flag }. staff_note is required when target is 'needs_changes';
   * ink_usage_ml / ink_stock_id are optional on the →ready transition.
   * @param {Object} data - { status, staff_note?, ink_usage_ml?, ink_stock_id? }
   */
  updateStatus: async (id, data) => {
    return await request(`/print-jobs/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Owner edits a needs_changes job's spec, which re-runs preflight and (on a
   * clean pass) resubmits. Returns { data, findings[] }.
   * @param {Object} data - { spec, brief_id?, title? }
   */
  updateSpec: async (id, data) => {
    return await request(`/print-jobs/${id}/spec`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Print briefs — printable presets carrying the preflight constraints. Students
 * receive active briefs only; the print TO / master may CRUD them.
 */
export const printBriefsAPI = {
  getAll: async () => {
    return await request('/print-briefs');
  },

  getById: async (id) => {
    return await request(`/print-briefs/${id}`);
  },

  create: async (data) => {
    return await request('/print-briefs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id, data) => {
    return await request(`/print-briefs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    return await request(`/print-briefs/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Print stock — consumables inventory (paper / ink / other) for the print room.
 * READ is open to print-room viewers; every WRITE is print TO / master only and
 * logs an immutable movement.
 */
export const printStockAPI = {
  /** List stock. Optional { kind: 'paper'|'ink'|'other' }. */
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(`/print-stock${qs ? `?${qs}` : ''}`);
  },

  /** Items at or below their low_threshold. */
  getLow: async () => {
    return await request('/print-stock/low');
  },

  /** Movement history, optionally filtered by { stock_id } or { job_id }. */
  getMovements: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(`/print-stock/movements${qs ? `?${qs}` : ''}`);
  },

  create: async (data) => {
    return await request('/print-stock', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Edit an item; a quantity change logs an 'adjust' movement. */
  update: async (id, data) => {
    return await request(`/print-stock/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /** Add stock; logs a 'restock' movement. @param {Object} data { delta, note? } */
  restock: async (id, data) => {
    return await request(`/print-stock/${id}/restock`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// AVAILABILITY BLACKOUTS API
// ============================================

export const blackoutsAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(`/blackouts${qs ? `?${qs}` : ''}`);
  },

  create: async (data) => {
    return await request('/blackouts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    return await request(`/blackouts/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// EQUIPMENT CHECKLISTS API
// ============================================

export const checklistsAPI = {
  get: async (equipmentId) => {
    return await request(`/checklists/${equipmentId}`);
  },

  save: async (equipmentId, { has_checklist, items }) => {
    return await request(`/checklists/${equipmentId}`, {
      method: 'PUT',
      body: JSON.stringify({ has_checklist, items }),
    });
  },
};

// ============================================
// SEMANTIC SEARCH API
// ============================================

export const searchAPI = {
  semantic: async ({ query, type = 'equipment', limit = 50 }) => {
    return await request('/search/semantic', {
      method: 'POST',
      body: JSON.stringify({ query, type, limit }),
    });
  },
};

// ============================================
// EQUIPMENT KITS API
// ============================================

export const equipmentKitsAPI = {
  /**
   * Get all equipment kits
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/kits?${queryString}` : '/kits';
    return await request(endpoint);
  },

  /**
   * Create new equipment kit (Admin only)
   */
  create: async (kitData) => {
    return await request('/kits', {
      method: 'POST',
      body: JSON.stringify(kitData),
    });
  },

  /**
   * Update equipment kit (Admin only)
   */
  update: async (id, kitData) => {
    return await request(`/kits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(kitData),
    });
  },

  /**
   * Delete equipment kit (Admin only)
   */
  delete: async (id) => {
    return await request(`/kits/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// AI CHAT API
// ============================================

export const aiChatAPI = {
  /**
   * One turn for a text AI task. Answers { success, response, source }.
   * @param {Array} messages - Array of message objects with role and content
   * @param {Object} options - { task: 'chat' | 'justification_analysis' | 'email_draft' | 'data_import', temperature? }
   */
  chat: async (messages, options = {}) => {
    return await request('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, ...options }),
    });
  },

  /**
   * Assess equipment condition from a photo
   * @param {string} image - Base64 encoded image
   * @param {Object} equipmentInfo - Optional equipment metadata
   */
  assessCondition: async (image, equipmentInfo = {}) => {
    return await request('/ai/assess-condition', {
      method: 'POST',
      body: JSON.stringify({ image, equipmentInfo }),
    });
  },

  /**
   * Answer a question with one of the server's fixed reports (the model only
   * picks the report). Answers { report, params, ran, columns, rows, rowCount, source }.
   * @param {string} question - Natural language question
   */
  naturalLanguageQuery: async (question) => {
    return await request('/ai/query', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  },

  /** GET /ai/query/catalogue — the reports a question can be answered with */
  getQueryCatalogue: async () => request('/ai/query/catalogue'),
};

// ============================================
// AI LOCAL RELAY API (DGX Sparks), used by services/aiRouter.js only
// ============================================

export const aiRelayAPI = {
  /** GET /ai/relay — { relay: { url, key, tasks } | null }; keep the key in memory only */
  getRelay: async () => request('/ai/relay', { cache: 'no-store' }),

  /** POST /ai/:task/prepare — { prepare_id, messages, max_tokens, temperature, model, relay_url } */
  prepare: async (task, input) => request(`/ai/${encodeURIComponent(task)}/prepare`, {
    method: 'POST',
    body: JSON.stringify(input || {}),
  }),

  /** POST /ai/:task/complete — the task's usual answer, built from the relay's reply */
  complete: async (task, body) => request(`/ai/${encodeURIComponent(task)}/complete`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
};

// ============================================
// AI CONFIGURATION API
// Wraps the /ai/available-models + /ai/settings endpoints used by AIConfiguration.jsx
// ============================================

export const aiConfigAPI = {
  /** GET /ai/available-models — Workers AI model catalogue */
  getAvailableModels: async () => {
    return await request('/ai/available-models');
  },

  /** GET /ai/settings — { settings: { ai_model_config, ... }, keys: { anthropic|openai|custom: { saved, last4? } } } */
  getConfig: async () => {
    return await request('/ai/settings');
  },

  /** PUT /ai/settings — persist the ai_model_config JSON */
  saveConfig: async (config) => {
    return await request('/ai/settings', {
      method: 'PUT',
      body: JSON.stringify({ key: 'ai_model_config', value: config }),
    });
  },

  /**
   * PUT /ai/provider-key — save (or, with '', remove) one provider's API key.
   * Write-only: the answer is { key: { saved, last4 } }, never the key.
   */
  saveProviderKey: async (provider, apiKey) => {
    return await request('/ai/provider-key', {
      method: 'PUT',
      body: JSON.stringify({ provider, apiKey }),
    });
  },

  /** GET /ai/status — is Workers AI binding available */
  getStatus: async () => {
    return await request('/ai/status');
  },
};

// ============================================
// FINES API
// ============================================

export const finesAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(qs ? `/fines?${qs}` : '/fines');
  },

  getSummary: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(qs ? `/fines/summary?${qs}` : '/fines/summary');
  },

  // The signed-in user's own fines and total (students and lecturers).
  getMine: () => request('/fines/mine'),
  getMyTotal: () => request('/fines/mine/total'),

  update: async (id, data) => {
    return await request(`/fines/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  markOverdue: async () => {
    return await request('/fines/mark-overdue', {
      method: 'POST',
    });
  },

  processOverdue: async () => {
    return await request('/fines/process-overdue', {
      method: 'POST',
    });
  },

  /** How many fines "Process Overdue Bookings" would raise now: { data: { count } } */
  getOverdueUnfined: () => request('/fines/overdue-unfined'),
};

// ============================================
// PURGE API (Master Admin only)
// ============================================

export const purgeAPI = {
  purgeUsers: async () => {
    return await request('/purge/users', {
      method: 'DELETE',
      body: JSON.stringify({ confirm: 'PURGE_USERS' }),
    });
  },

  purgeEquipment: async () => {
    return await request('/purge/equipment', {
      method: 'DELETE',
      body: JSON.stringify({ confirm: 'PURGE_EQUIPMENT' }),
    });
  },

  purgeAll: async () => {
    return await request('/purge/all', {
      method: 'DELETE',
      body: JSON.stringify({ confirm: 'PURGE_ALL' }),
    });
  },
};

// ============================================
// INTER-AREA EQUIPMENT LOANS API
// Admin-to-admin borrowing between areas (see functions/routes/equipment-loans.js).
// Every response is read off `data` — the generic /equipment prefix normaliser
// does not apply to this endpoint's shapes.
// ============================================

export const equipmentLoansAPI = {
  // The department admins this viewer may ask, each with the area they run.
  // The SERVER decides who is eligible; the form only renders the list.
  getLenders: () => request('/equipment-loans/lenders'),
  getLendable: (subAreaId, startDate, endDate) => request(
    `/equipment-loans/lendable?sub_area_id=${encodeURIComponent(subAreaId)}`
    + `&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
  ),
  getAll: () => request('/equipment-loans'),
  create: (data) => request('/equipment-loans', { method: 'POST', body: JSON.stringify(data) }),
  approve: (batch, note) => request(`/equipment-loans/batch/${encodeURIComponent(batch)}/approve`, {
    method: 'PUT', body: JSON.stringify({ note: note || null }),
  }),
  decline: (batch, note) => request(`/equipment-loans/batch/${encodeURIComponent(batch)}/decline`, {
    method: 'PUT', body: JSON.stringify({ note: note || null }),
  }),
  cancel: (batch) => request(`/equipment-loans/batch/${encodeURIComponent(batch)}`, { method: 'DELETE' }),
  // The lender ends an approved loan early; the items are free again from today.
  end: (batch) => request(`/equipment-loans/batch/${encodeURIComponent(batch)}/end`, { method: 'PUT' }),
};

// ============================================
// EQUIPMENT OVERVIEW API
// ============================================
// What is out, due back and coming up, scoped by the server to the viewer's
// department. Read off `data`: { window, counts, out, due, ready, upcoming }.
export const equipmentOverviewAPI = {
  get: ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return request(`/equipment-overview${qs ? `?${qs}` : ''}`);
  },
};

// ============================================
// STAFF BLOCK BOOKINGS API
// Hold N units of a model for a class; the department admin approves the
// whole block in one go. Who may is the SERVER's answer (`getPermission`).
// ============================================

export const blockBookingsAPI = {
  getPermission: () => request('/block-bookings/permission'),
  getStock: (startDate, endDate) => request(
    `/block-bookings/stock?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
  ),
  getAll: (status) => request(`/block-bookings${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (data) => request('/block-bookings', { method: 'POST', body: JSON.stringify(data) }),
  approve: (ref) => request(`/block-bookings/${encodeURIComponent(ref)}/approve`, { method: 'PUT', body: '{}' }),
  deny: (ref, reason) => request(`/block-bookings/${encodeURIComponent(ref)}/deny`, {
    method: 'PUT', body: JSON.stringify({ reason }),
  }),
  withdraw: (ref) => request(`/block-bookings/${encodeURIComponent(ref)}`, { method: 'DELETE' }),
  // Admin controls: the per-area switch and the per-person override.
  getAreaSwitches: () => request('/department-settings/staff-block-booking/areas'),
  setAreaSwitch: (areaId, enabled) => request(
    `/department-settings/${encodeURIComponent(areaId)}/staff_block_booking`,
    { method: 'PUT', body: JSON.stringify({ value: enabled ? '1' : '0' }) }
  ),
  getStaff: () => request('/staff-permissions/block-booking/staff'),
  setStaffOverride: (userId, value) => request(`/staff-permissions/block-booking/user/${encodeURIComponent(userId)}`, {
    method: 'PUT', body: JSON.stringify({ value }),
  }),
};

// ============================================
// STRIKES API
// ============================================

export const strikesAPI = {
  getStudentStatus: (userId) => request(`/strikes/student/${userId}`),
  // Issuing a strike lives on usersAPI.addStrike (POST /users/:id/strikes).
  /** POST /strikes/:id/revoke — { data: { strike_id, user_id, strike_count, blacklist_until } } */
  revoke: (strikeId, reason) => request(`/strikes/${strikeId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
};

// ============================================
// STAFF PERMISSIONS API
// ============================================

export const staffPermissionsAPI = {
  // Staff permissions (staff_permissions column)
  updateStaffPermissions: (userId, permissions) => request(`/staff-permissions/user/${userId}`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
  resetStaffPermissions: (userId) => request(`/staff-permissions/user/${userId}`, { method: 'PUT', body: JSON.stringify({ reset: true }) }),

  // Admin permissions (admin_permissions column) — uses /admin-user/ prefix within same mount
  updateAdminPermissions: (userId, permissions) => request(`/staff-permissions/admin-user/${userId}`, { method: 'PUT', body: JSON.stringify({ permissions }) }),

  // User lists
  getAllStaff: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/staff-permissions/staff${query ? '?' + query : ''}`);
  },
  getAllAdmins: () => request('/staff-permissions/admins'),

  // Audit log
  getAuditLog: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/staff-permissions/audit-log${query ? '?' + query : ''}`);
  },
};

// ============================================
// SPACES API
// ============================================

export const spacesAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/spaces${query ? '?' + query : ''}`);
  },
  // The top-level departments THIS admin may put a room in. The server owns the
  // answer — the browser never rebuilds scope from the token.
  getDepartments: () => request('/spaces/departments'),
  create: (data) => request('/spaces', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/spaces/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStatus: (id, status) => request(`/spaces/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  delete: (id) => request(`/spaces/${id}`, { method: 'DELETE' }),
  getAvailability: (id, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/spaces/${id}/availability${query ? '?' + query : ''}`);
  },
  // Every booking holding a room the caller can see — lent-in rooms included,
  // holder details only where department isolation allows.
  getOccupancy: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/spaces/occupancy${query ? '?' + query : ''}`);
  },
};

// ============================================
// SPACE BOOKINGS API
// ============================================

export const spaceBookingsAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/space-bookings${query ? '?' + query : ''}`);
  },
  // May I book a room for myself? The server's own answer — the portal shows
  // its buttons from this rather than working the department scope out here.
  myRoomAccess: () => request('/space-bookings/room-access'),
  create: (data) => request('/space-bookings', { method: 'POST', body: JSON.stringify(data) }),
  // Admin books a room on behalf of a student. Server-side auto-approved; the
  // student and the room are both re-checked against the admin's scope there.
  adminCreate: (data) => request('/space-bookings/admin-create', { method: 'POST', body: JSON.stringify(data) }),
  approve: (id, data = {}) => request(`/space-bookings/${id}/approve`, { method: 'PUT', body: JSON.stringify(data) }),
  deny: (id, data) => request(`/space-bookings/${id}/deny`, { method: 'PUT', body: JSON.stringify(data) }),
  cancel: (id) => request(`/space-bookings/${id}`, { method: 'DELETE' }),
};

// ============================================
// BOOKING TEMPLATES API
// ============================================

export const bookingTemplatesAPI = {
  /**
   * Get all templates for the authenticated user (own + public in dept)
   */
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(qs ? `/booking-templates?${qs}` : '/booking-templates');
  },

  /**
   * Create a new template
   * @param {Object} data - { name, description, equipment_ids, default_duration_days, notes, is_public }
   */
  create: async (data) => {
    return await request('/booking-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a template (owner or admin only)
   */
  delete: async (id) => {
    return await request(`/booking-templates/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// AUDIT LOG API (Master Admin only)
// ============================================

export const auditLogAPI = {
  /**
   * Get audit log entries with optional filters and pagination
   * @param {Object} params - user_id, action, start_date, end_date, limit, offset
   */
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return await request(qs ? `/audit-log?${qs}` : '/audit-log');
  },

  /**
   * Get distinct action types for the filter dropdown
   */
  getActionTypes: async () => {
    return await request('/audit-log/actions');
  },
};

// ============================================
// MAINTENANCE API (Admin only)
// ============================================

export const maintenanceAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/maintenance${query ? '?' + query : ''}`);
  },
  create: (data) => request('/maintenance', { method: 'POST', body: JSON.stringify(data) }),
  complete: (id, notes) => request(`/maintenance/${id}/complete`, { method: 'PUT', body: JSON.stringify({ notes }) }),
  cancel: (id) => request(`/maintenance/${id}/cancel`, { method: 'PUT' }),
  delete: (id) => request(`/maintenance/${id}`, { method: 'DELETE' }),
};

// ============================================
// BACKUP API (Master Admin only)
// ============================================

export const backupAPI = {
  /**
   * Export all tables as a JSON backup
   */
  exportAll: () => request('/backup/export'),

  /**
   * Import data from a JSON backup
   * @param {{ tables: Object, clear_existing: boolean }} data
   */
  importData: (data) => request('/backup/import', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  /**
   * Export a single table as JSON
   * @param {string} tableName
   */
  exportTable: (tableName) => request(`/backup/table/${tableName}`),

  /**
   * The tables a backup covers (read from the database, not a fixed list)
   */
  listTables: () => request('/backup/tables'),
};

// ============================================
// SYSTEM HEALTH API (master admin only)
// ============================================

export const systemHealthAPI = {
  /** GET /system-health — system metrics */
  getHealth: () => request('/system-health'),
  /** GET /system-health/recent-activity — last 10 admin actions */
  getRecentActivity: () => request('/system-health/recent-activity'),
};

// ============================================
// EMAIL STATS API (master admin only)
// ============================================

/**
 * Overdue reminders sent by hand from the Return page (department and master
 * admins). `send` takes { booking_ids } or { all: true }, and
 * { confirm_resend: true } once the desk has confirmed a same-day re-send.
 */
export const overdueRemindersAPI = {
  list: () => request('/overdue-reminders'),
  send: (body) => request('/overdue-reminders', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
};

export const emailStatsAPI = {
  /** GET /email-stats — sends today / last 14 days / this month vs the plan limits */
  get: () => request('/email-stats'),
};

// Export token management functions
export const tokenManager = {
  getToken,
  setToken,
  removeToken,
  hasToken: () => !!getToken(),
};

export default {
  authAPI,
  deviceTokensAPI,
  equipmentAPI,
  bookingsAPI,
  usersAPI,
  departmentsAPI,
  analyticsAPI,
  systemSettingsAPI,
  equipmentKitsAPI,
  aiChatAPI,
  equipmentLoansAPI,
  strikesAPI,
  staffPermissionsAPI,
  spacesAPI,
  spaceBookingsAPI,
  auditLogAPI,
  bookingTemplatesAPI,
  maintenanceAPI,
  backupAPI,
  systemHealthAPI,
  emailStatsAPI,
  labComputersAPI,
  trainingRecordsAPI,
  inductionTypesAPI,
  inductionSessionsAPI,
  attendanceAPI,
  printJobsAPI,
  printBriefsAPI,
  printStockAPI,
  inductionRequestsAPI,
  tokenManager,
};
